import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useScheduleAppointment } from '@/hooks/use-schedule-appointment';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { CalendarView } from '@/components/appointment/CalendarView';
import TimeSlotPicker from '@/components/appointment/TimeSlotPicker';
import { type DateRange } from 'react-day-picker';

const ScheduleAppointment: React.FC = () => {
  const locationRoute = useLocation(); // Renamed to avoid conflict with location variable from hook
  const testOrderId = locationRoute.state?.testOrderId;

  const {
    providers, 
    locations, 
    timeSlots, 
    selectedDate, 
    selectedProviderId, 
    selectedLocationId, 
    selectedTimeSlotId,
    selectedTimeSlot,
    appointmentNotes,
    selectDate, 
    selectProvider, 
    selectLocation, 
    selectTimeSlot, 
    setAppointmentNotes,
    scheduleAppointment,
    isLoadingProviders, 
    isLoadingLocations, 
    isLoadingTimeSlots, 
    isScheduling,
    providersError,
    locationsError,
    timeSlotsError,
    schedulingError
  } = useScheduleAppointment({ testOrderId });

  // Create date range as a memoized value to prevent re-renders
  const dateRange: DateRange = useMemo(() => {
    const today = new Date();
    return { 
      from: today, 
      to: new Date(today.getFullYear(), today.getMonth(), today.getDate() + 30) 
    };
  }, []);
  
  // Handler for time slot selection that passes the full slot object to the hook
  const handleSelectTimeSlot = (slot: any) => {
    selectTimeSlot(slot.id);
  };

  // Find the selected provider and location objects
  const selectedProvider = providers.find(p => p.id === selectedProviderId);
  const selectedLocation = locations.find(l => l.id === selectedLocationId);

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">Schedule New Appointment</CardTitle>
          <p className="text-muted-foreground mt-1">
            Select a provider to see their available location and appointment times.
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Provider Selection */}
          <div className="space-y-2">
            <label htmlFor="provider-select" className="text-sm font-medium">Select Provider</label>
            
            {/* Custom provider select with visible display of selected value */}
            <div className="relative">
              <Select 
                value={selectedProviderId || ''}
                onValueChange={selectProvider}
                disabled={isLoadingProviders}
              >
                <SelectTrigger aria-label="Select Provider" className="w-full">
                  {isLoadingProviders ? (
                    <span className="text-muted-foreground">Loading providers...</span>
                  ) : selectedProvider ? (
                    <span>
                      {selectedProvider.name} 
                      {selectedProvider.specialty && ` (${selectedProvider.specialty})`}
                    </span>
                  ) : (
                    <span className="text-muted-foreground">Select a provider</span>
                  )}
                </SelectTrigger>
                <SelectContent>
                  {providersError && <SelectItem value="error" disabled>Error loading providers</SelectItem>}
                  {providers.map((provider) => (
                    <SelectItem key={provider.id} value={provider.id}>
                      {provider.name} {provider.specialty && `(${provider.specialty})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Location Selection */}
          <div className="space-y-2">
            <label htmlFor="location-select" className="text-sm font-medium">Select Location</label>
            
            {/* Custom location select with visible display of selected value */}
            <div className="relative">
              <Select 
                value={selectedLocationId || ''}
                onValueChange={selectLocation}
                disabled={isLoadingLocations || !!selectedProviderId}
              >
                <SelectTrigger aria-label="Select Location" className="w-full">
                  {isLoadingLocations ? (
                    <span className="text-muted-foreground">Loading locations...</span>
                  ) : selectedLocation ? (
                    <span>
                      {selectedLocation.name} - {selectedLocation.city}, {selectedLocation.state}
                    </span>
                  ) : (
                    <span className="text-muted-foreground">Select a location</span>
                  )}
                </SelectTrigger>
                <SelectContent>
                  {locationsError && <SelectItem value="error" disabled>Error loading locations</SelectItem>}
                  {locations.map((locationItem) => (
                    <SelectItem key={locationItem.id} value={locationItem.id}>
                      {locationItem.name} - {locationItem.city}, {locationItem.state}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {/* Information message when provider is selected */}
              {selectedProviderId && selectedProvider && (
                <p className="text-xs text-muted-foreground mt-1">
                  Dr. {selectedProvider.name.replace('Dr. ', '')} practices at {selectedLocation?.name || 'this location'}
                </p>
              )}
            </div>
          </div>

          {/* Calendar and Time Slot Selection - Always render but conditionally enable */} 
          <div className={`grid grid-cols-1 md:grid-cols-2 gap-6 ${(!selectedProviderId || !selectedLocationId) ? 'opacity-50 pointer-events-none' : ''}`}>
            <div className="space-y-2">
              <label className="text-sm font-medium">Select Date</label>
              <CalendarView 
                selectedDate={selectedDate}
                onSelectDate={selectDate}
                dateRange={dateRange}
                disabledDates={[]} 
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Select Time</label>
              {timeSlotsError && <p className="text-red-500 text-sm">Error loading time slots.</p>} 
              <TimeSlotPicker 
                timeSlots={timeSlots}
                selectedTimeSlot={selectedTimeSlot}
                onSelectTimeSlot={handleSelectTimeSlot}
                isLoading={isLoadingTimeSlots}
              />
            </div>
          </div>

          {/* Appointment Notes */} 
          <div className="space-y-2">
            <label htmlFor="appointment-notes" className="text-sm font-medium">Notes (Optional)</label>
            <Textarea 
              id="appointment-notes"
              placeholder="Add any notes for your appointment (e.g., reason for visit if not related to a test order)"
              value={appointmentNotes}
              onChange={(e) => setAppointmentNotes(e.target.value)}
              className="min-h-[80px]"
            />
          </div>

          {/* Scheduling Error Display */}
          {schedulingError && (
            <Alert variant="destructive">
              <AlertTitle>Scheduling Failed</AlertTitle>
              <AlertDescription>
                {schedulingError instanceof Error 
                  ? schedulingError.message 
                  : 'An error occurred while scheduling your appointment. Please try again.'}
              </AlertDescription>
            </Alert>
          )}

          {/* Submit Button */}
          <Button 
            className="w-full md:w-auto"
            onClick={scheduleAppointment}
            disabled={!selectedTimeSlotId || isScheduling}
          >
            {isScheduling ? 'Scheduling...' : 'Schedule Appointment'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default ScheduleAppointment; 